import CloseIcon from "@mui/icons-material/Close";

import { RobotoIconButton, RobotoTypography } from "@/shared/components";
import { DataSourceSelector } from "@/shared/components/visualization/controls/DataSourceSelector";
import { MessagePathNode, PathParser } from "@/shared/domain/topics";
import { actions, useVizDispatch } from "@/shared/state/visualization";
import { TopicData } from "@/shared/state/visualization/schema/v1";

import { isViewableAsRawMessage } from "../../Panel/predicates";

import styles from "./MessagePaths.module.css";
import { useIsDuplicateMessagePath } from "./useIsDuplicateMessagePath";

interface MessagePathsProps {
  panelId: string;
  messagePaths: TopicData[];
}

export function MessagePaths({ panelId, messagePaths }: MessagePathsProps) {
  const isExisting = useIsDuplicateMessagePath(messagePaths);

  if (messagePaths.length === 0) {
    return null;
  }

  return (
    <div className={styles.messagePaths}>
      {messagePaths.map((entry, i) => {
        return (
          <MessagePathEntry
            key={entry.messagePath.id}
            entry={entry}
            panelId={panelId}
            label={`Message Path ${i + 1}`}
            isExisting={isExisting}
            messagePaths={messagePaths}
          />
        );
      })}
    </div>
  );
}

interface MessagePathEntryProps {
  panelId: string;
  entry: TopicData;
  label: string;
  isExisting: (nextTopicData: TopicData) => boolean;
  messagePaths: TopicData[];
}

function MessagePathEntry({
  panelId,
  entry,
  label,
  isExisting,
  messagePaths,
}: MessagePathEntryProps) {
  const dispatch = useVizDispatch();

  const initialFile = entry.topic.association.association_id;
  const initialPath = [
    entry.topic.name,
    MessagePathNode.partsToString(entry.messagePath.parts),
  ].join(PathParser.TOPIC_SEPARATOR);

  return (
    <div className={styles.entry}>
      <div className={styles.entryHeader}>
        <RobotoTypography variant="overline">{label}</RobotoTypography>
        <RobotoIconButton
          aria-label="remove"
          className={styles.iconButton}
          eventName="RemoveMessagePathClicked"
          onClick={() => {
            dispatch(
              actions.removeMessagePathFromRawMessagePanel(
                panelId,
                entry.messagePath.id,
              ),
            );
          }}
          title="Remove Message Path"
        >
          <CloseIcon fontSize="inherit" />
        </RobotoIconButton>
      </div>
      <div>
        <RobotoTypography component={"label"} variant="subtitle2">
          Message Path
        </RobotoTypography>
        <DataSourceSelector
          initialFile={initialFile}
          initialPath={initialPath}
          onChange={(messagePathNode) => {
            dispatch(
              actions.setDataSource({
                topicData: messagePathNode.toTopicData(),
                panelId,
                segmentId: entry.messagePath.id,
              }),
            );
          }}
          optionFilter={(messagePathNode) => {
            if (messagePathNode.data.message_path_id === entry.messagePath.id) {
              return true;
            }
            if (isExisting(messagePathNode.toTopicData())) {
              return false;
            }
            if (
              messagePaths.length > 0 &&
              messagePathNode.topic.id !== messagePaths[0].topic.id
            ) {
              return false;
            }
            return isViewableAsRawMessage(messagePathNode);
          }}
        />
      </div>
    </div>
  );
}
